// TypeScript mirrors of the metrics domain types in
// `packages/roboto/src/roboto/domain/metrics/record.py`. Field names match the
// wire format exactly: `created`/`modified`, `published`/`published_by`,
// `min_timestamp_ns`/`max_timestamp_ns`, snake_case throughout.

import type { PaginatedResponse } from "@/shared/http";
import { type Time } from "@/shared/time/time";
import type { Condition } from "@/types";

// TODO(ruslan): raise the cap to a value the new filter backend works well with
/**
 * The most named groups a grouped metrics query returns — `MAX_METRIC_GROUPS`
 * in `packages/roboto/src/roboto/domain/metrics/record.py`, which the server
 * enforces. The groups kept are those with the most sessions in the window;
 * the "Not set" group rides on top of the cap. Every card uses this same cap.
 */
export const MAX_METRIC_GROUPS = 100;

/**
 * How a grouped response reports the group cap. Both are absent from a server
 * that predates the cap, whose responses were never capped.
 */
export interface GroupCapFields {
  /** Distinct non-null group keys with data in the response's window, capped
   *  or not. An aggregate's window is its whole periods, which can reach past
   *  the requested bounds. `null` on an ungrouped response. */
  total_groups?: number | null;
  /** Whether groups beyond {@link MAX_METRIC_GROUPS} were left out. */
  groups_truncated?: boolean;
}

export const PERIODS = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
] as const;
export type Period = (typeof PERIODS)[number];

/** The singular noun for one bucket of a period — "week" for `weekly`. The
 *  period names themselves are adverbs and don't read in running text. */
export const PERIOD_NOUN: Record<Period, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

/** The days a week can start on, Monday first, as the backend `DayOfWeek`
 *  StrEnum spells and orders them. */
export const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const NUMERIC_AGGREGATIONS = [
  "sum",
  "mean",
  "max",
  "min",
  "count",
] as const;
export type NumericAggregation = (typeof NUMERIC_AGGREGATIONS)[number];

// Wire values from the backend `MetricTimeFilter` StrEnum: the Pydantic
// validator accepts the enum *value* (lowercase), not the Python attribute
// name. Keep these as the literal strings the server expects.
export const METRIC_TIME_FILTERS = ["start_time", "end_time"] as const;
export type MetricTimeFilter = (typeof METRIC_TIME_FILTERS)[number];

/**
 * Columns a raw metrics query can be ordered by. Wire values of the backend's
 * `sort_by` field.
 *
 * - `time`: the session time column the request's `time_filter` picks (session
 *   start for `start_time`, session end for `end_time`).
 * - `value`, `published`, `device_id`, `session_id`, `invocation_id`: the
 *   record's own field of that name.
 *
 * Ties break on `session_id`, then, when a request names several metrics, on
 * `metric_id`. Nulls (`device_id`, `invocation_id`) sort last ascending and
 * first descending.
 */
export type MetricSortField =
  | "time"
  | "value"
  | "published"
  | "device_id"
  | "session_id"
  | "invocation_id"
  // The request's `group_by` value per record; only valid with `group_by`.
  | "group_key";

/**
 * The ways a chart card can draw its series, in the order the Display picker
 * offers them.
 *
 * `linearAxis` says whether the display can be drawn on a TIME-PROPORTIONAL
 * (linear) x-axis, which is the axis an unaggregated chart uses. Bar cannot:
 * Chart.js sizes bars from the smallest interval between points, and on an
 * irregularly spaced axis that collapses them to hairlines. Line and scatter
 * both position their marks pointwise, so neither cares how the axis is laid
 * out.
 */
export const CHART_DISPLAY_TYPES = [
  { value: "line", label: "Line chart", linearAxis: true },
  { value: "bar", label: "Bar chart", linearAxis: false },
  { value: "scatter", label: "Scatter plot", linearAxis: true },
] as const;
export type ChartDisplayType = (typeof CHART_DISPLAY_TYPES)[number]["value"];

export interface MetricDefinitionRecord {
  org_id: string;
  metric_id: string;
  /** Unique (per-org) name for this metric. */
  name: string;
  description: string | null;
  /**
   * Unit of measure for every value recorded under this metric, e.g. "%",
   * "ms", "m/s". Free-form and unvalidated; `null` means unitless.
   */
  unit: string | null;
  created_by: string;
  created: string; // ISO 8601 with TZ
  modified_by: string;
  modified: string; // ISO 8601 with TZ
}

export interface MetricRecord {
  org_id: string;
  metric_id: string;
  // Fully qualified, dot-separated metric name resolved server-side from the
  // associated `MetricDefinitionRecord`. Mirrors the `name` field added to the
  // Python `MetricRecord` in the SDK.
  name: string;
  session_id: string;
  // The source session's raw int-nanosecond bounds (Unix epoch ns). `null`
  // while the session includes no files. Mirror of
  // SessionRecord.min_timestamp_ns / max_timestamp_ns.
  min_timestamp_ns: bigint | null;
  max_timestamp_ns: bigint | null;
  // The times a `time_filter` of `start_time` and of `end_time` place this
  // data point at, in Unix-epoch ns: the session's start and end, or the time
  // the session was created while it has no time bounds. A query or aggregate
  // window holds the data point when it holds this time, and an aggregate
  // counts it in the period that holds this time.
  session_start_time_ns: bigint;
  session_end_time_ns: bigint;
  value: number;
  // Unit of measure for `value`, resolved server-side from the associated
  // `MetricDefinitionRecord` — same JOIN that resolves `name`, so a value can
  // be labeled without a second lookup. `null` means unitless.
  unit: string | null;
  device_id?: string | null;
  invocation_id?: string | null;
  // The value of the request's `group_by` field for this data point, cast to text server-side so
  // one nullable string covers every groupable field type. Null on an ungrouped query and on a
  // data point carrying no value for the field. Mirrors NumericAggregateMetricRecord.group_key.
  group_key?: string | null;
  published_by: string;
  published: string; // ISO 8601 with TZ
}

export interface NumericAggregateMetricRecord {
  metric_id: string;
  name: string;
  period: Period;
  // Inclusive start and exclusive end of this period bucket, in Unix-epoch
  // nanoseconds: midnight in the request's `time_zone` on the first day of
  // this period and of the next.
  start_time: bigint;
  end_time: bigint;
  total: number;
  value: number;
  // Unit of measure for `value`, resolved from the aggregated metric's
  // definition alongside `name`. `null` means unitless.
  unit: string | null;
  aggregation: NumericAggregation;
  // The value of the request's `group_by` field for the rows behind this
  // bucket, cast to text server-side so one nullable string covers every
  // groupable field type. Absent entirely on an ungrouped response; `null`
  // when the field was unset on those rows, which is a series of its own
  // rather than rows to drop.
  group_key?: string | null;
}

export interface NumericAggregateMetricsResponse extends GroupCapFields {
  aggregation: NumericAggregation;
  records: NumericAggregateMetricRecord[];
}

/** One page of a raw metrics query. The group-cap fields describe the whole
 *  query, so every page carries the same values. */
export interface MetricQueryPage
  extends PaginatedResponse<MetricRecord>,
    GroupCapFields {}

// -------- Request shapes --------

export interface CreateMetricDefinitionRequest {
  name: string;
  description?: string;
  unit?: string;
}

export interface UpdateMetricDefinitionRequest {
  description?: string;
  /** Omit to leave unchanged (the server's NotSet default); `null` clears it. */
  unit?: string | null;
}

export interface MetricEntry {
  name: string;
  value: number;
}

export interface PublishMetricsRequest {
  session_id: string;
  /** Omit entirely to let the server infer from the session's attached devices.
   *  Pass `null` to record without a device. Pass a string to bind to a device. */
  device_id?: string | null;
  metrics: MetricEntry[];
}

export interface QueryMetricsRequest {
  name: string;
  time_filter?: MetricTimeFilter;
  /** Inclusive window start, Unix-epoch ns. `bigint` to preserve full ns
   *  precision end-to-end (the backend field is an arbitrary-precision `int`);
   *  `MetricsService.queryMetrics` serializes it as a bare integer literal via
   *  `rawJSON` rather than a lossy `number`. */
  start_time_ns?: bigint;
  /** Exclusive window end, Unix-epoch ns. See `start_time_ns`. */
  end_time_ns?: bigint;
  max_results?: number;
  /**
   * Column to order results by (server default: `"time"`). See
   * {@link MetricSortField}.
   *
   * Bound to page tokens exactly like `descending`: the server rejects a
   * continuation whose sort differs from the one that issued its token.
   */
  sort_by?: MetricSortField;
  /**
   * Sort by `sort_by` in descending order, e.g. the most recent sessions first
   * under the default `"time"` sort (server default: ascending).
   *
   * A page token is bound to the direction that issued it — the server rejects
   * a continuation that flips it — so this is fixed for the life of a
   * pagination, which is why it belongs in the request object (and therefore
   * in the React Query key) rather than being passed per page.
   */
  descending?: boolean;
  include_device_ids?: string[] | null;
  /** Server-side: only a list is accepted; `null` is rejected at validation time
   *  because `session_id` is non-nullable in the DB. */
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
  /** Conditions to filter metrics to query */
  condition?: Condition;
  /**
   * Project this field's value onto each returned data point, under
   * `MetricRecord.group_key`.
   *
   * Unlike the aggregation's `group_by`, this collapses nothing — a raw query already returns one
   * data point per session — so it changes neither which rows come back nor how many. Same field
   * vocabulary as the aggregation's.
   */
  group_by?: string;
}

/**
 * Experimental. The `POST v1/metrics/query` body that reads several metrics
 * in one paginated request: `names` (at least one, none repeated) in place of
 * `name`, every other field as in `QueryMetricsRequest`; the server rejects a
 * body carrying both. Each returned record carries the `name` of its metric.
 * Replay a page token only with the `names` list it was issued under.
 */
export interface QueryMetricsByNamesRequest
  extends Omit<QueryMetricsRequest, "name"> {
  names: string[];
}

/** A `POST v1/metrics/query` body: `name` to read one metric, `names` to
 *  read several. */
export type MetricsQueryRequest =
  | QueryMetricsRequest
  | QueryMetricsByNamesRequest;

export interface AggregateMetricsRequest {
  name: string;
  period: Period;
  aggregation: NumericAggregation;
  time_filter?: MetricTimeFilter;
  /** Inclusive window start. Accepts a Date, epoch MILLISECONDS, an ISO 8601
   *  string, or epoch nanoseconds as a bigint —
   *  `MetricsService.aggregateMetric` coerces to ns as it serializes. */
  start_time: Time;
  /** Exclusive window end. See `start_time`. */
  end_time: Time;
  include_device_ids?: string[] | null;
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
  /** Conditions to aggregate on filtered metrics */
  condition?: Condition;
  /**
   * Split each time bucket by the distinct values of this field, returning one
   * record per (bucket, value) pair with the value on `group_key`.
   *
   * The field is an entity-prefixed path, as `condition` writes it:
   * `device.device_id`, `session.custom.<name>` or `device.custom.<name>`.
   * `metricGroupByFieldKind` accepts these forms; the server also refuses a
   * custom field that is not a Ready String, Enum or Boolean field. Without
   * `group_by`, each bucket aggregates every matching data point of its
   * period and carries no `group_key`.
   */
  group_by?: string;
  /** IANA name of the time zone in which each period starts at midnight,
   *  such as `"America/Los_Angeles"`. The server refuses a name not in the
   *  IANA time zone database, and refuses `CET`, `EET`, `MET`, and `WET`,
   *  which it would read as fixed UTC offsets without daylight saving time.
   *  Server default: `"UTC"`. */
  time_zone?: string;
  /** The day each week starts on when `period` is weekly. Server default:
   *  `"monday"`. */
  week_start?: DayOfWeek;
  /** Whether the server widens the window to whole periods, so the first and
   *  last buckets aggregate every data point of their period, including those
   *  of sessions outside `start_time` and `end_time`. `false` aggregates only
   *  the data points inside the window, the same ones a `QueryMetricsRequest`
   *  with the same window, `time_filter` and filters returns. Each bucket's
   *  `start_time` and `end_time` are its whole period either way. Server
   *  default: `true`. */
  widen_to_whole_periods?: boolean;
}
